import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  CheckCircle, X, Copy, Mail, ExternalLink, Share2,
  MessageCircle, Zap
} from 'lucide-react';
import { toast } from 'sonner';
import axios from '../lib/axios';
import { API, useLanguage } from '../App';
import { ComingSoonOverlay } from './BoostComponents';

// Boost translations for ShareSuccessModal
const boostTranslations = {
  en: {
    boost_your_listing: 'Boost your listing',
    get_more_visitors: 'Get more visitors and sell faster',
    boost: 'Boost',
    payment_coming_soon: 'Payment feature coming soon. We are integrating with Sentoo for secure payments.',
    boost_requested: 'Boost requested!',
    boost_not_available: 'Boost feature is currently not available',
    boost_error: 'Something went wrong while boosting',
    view_my_listing: 'View my listing',
    share_later: 'Share later',
    coming_soon: 'Coming Soon',
    boost_visibility: 'Boost your visibility',
  },
  nl: {
    boost_your_listing: 'Boost je advertentie',
    get_more_visitors: 'Krijg meer bezoekers en verkoop sneller',
    boost: 'Boost',
    payment_coming_soon: 'Betaalfunctie komt binnenkort beschikbaar. We integreren met Sentoo voor veilige betalingen.',
    boost_requested: 'Boost aangevraagd!',
    boost_not_available: 'Boost functie is momenteel niet beschikbaar',
    boost_error: 'Er ging iets mis bij het boosten',
    view_my_listing: 'Bekijk mijn advertentie',
    share_later: 'Later delen',
    coming_soon: 'Coming Soon',
    boost_visibility: 'Boost je zichtbaarheid',
  },
  es: {
    boost_your_listing: 'Impulsa tu anuncio',
    get_more_visitors: 'Obtén más visitantes y vende más rápido',
    boost: 'Impulsar',
    payment_coming_soon: 'Función de pago próximamente. Estamos integrando Sentoo para pagos seguros.',
    boost_requested: '¡Impulso solicitado!',
    boost_not_available: 'La función de impulso no está disponible actualmente',
    boost_error: 'Algo salió mal al impulsar',
    view_my_listing: 'Ver mi anuncio',
    share_later: 'Compartir más tarde',
    coming_soon: 'Próximamente',
    boost_visibility: 'Aumenta tu visibilidad',
  }
};

// Brand colors for share buttons
const SHARE_BUTTONS = [
  {
    id: 'whatsapp',
    name: 'WhatsApp',
    icon: () => (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
      </svg>
    ),
    color: 'bg-[#25D366] hover:bg-[#20BD5A]',
    getUrl: (url, text) => `https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}`
  },
  {
    id: 'facebook',
    name: 'Facebook',
    icon: () => (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
      </svg>
    ),
    color: 'bg-[#1877F2] hover:bg-[#166FE5]',
    getUrl: (url) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`
  },
  {
    id: 'twitter',
    name: 'X',
    icon: () => (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
      </svg>
    ),
    color: 'bg-black hover:bg-gray-800',
    getUrl: (url, text) => `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`
  },
  {
    id: 'email',
    name: 'Email',
    icon: Mail,
    color: 'bg-gray-600 hover:bg-gray-700',
    getUrl: (url, text) => `mailto:?subject=${encodeURIComponent('Check dit uit op YourBuySite!')}&body=${encodeURIComponent(text + '\n\n' + url)}`
  }
];

/**
 * ShareSuccessModal
 * 
 * Shown after successful listing creation with share options
 * Uses Web Share API on mobile for native sharing experience
 */
const ShareSuccessModal = ({ 
  isOpen, 
  onClose, 
  listingId, 
  listingSlug,
  listingTitle 
}) => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [copied, setCopied] = useState(false);
  const [showConfetti, setShowConfetti] = useState(true);
  const [boosting, setBoosting] = useState(false);

  // Helper for boost translations
  const tb = (key) => boostTranslations[language]?.[key] || boostTranslations.en[key] || key;

  // Generate listing URL
  const listingUrl = `${window.location.origin}/listing/${listingSlug || listingId}`;
  const shareText = 'Check mijn advertentie op YourBuySite:';

  // Check if Web Share API is available (mobile)
  const canNativeShare = typeof navigator !== 'undefined' && navigator.share;

  useEffect(() => {
    if (isOpen) {
      setShowConfetti(true);
      // Hide confetti after animation
      const timer = setTimeout(() => setShowConfetti(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Track share action
  const trackShare = async (platform) => {
    try {
      await axios.post(`${API}/track-share`, {
        listing_id: listingId,
        platform,
        action: 'share_click'
      });
    } catch (error) {
      // Silent fail - analytics shouldn't block UX
      console.log('Share tracking failed:', error);
    }
  };

  // Handle native share (mobile)
  const handleNativeShare = async () => {
    try {
      await navigator.share({
        title: listingTitle || 'Mijn advertentie op YourBuySite',
        text: shareText,
        url: listingUrl
      });
      trackShare('native');
      toast.success('Gedeeld!');
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.log('Share failed:', error);
      }
    }
  };

  // Handle share button click
  const handleShare = (button) => {
    trackShare(button.id);
    const url = button.getUrl(listingUrl, shareText);
    window.open(url, '_blank', 'width=600,height=400');
  };

  // Handle copy link
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(listingUrl);
      setCopied(true);
      trackShare('copy');
      toast.success('Link gekopieerd!');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = listingUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      trackShare('copy');
      toast.success('Link gekopieerd!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Handle view listing
  const handleViewListing = () => {
    onClose();
    navigate(`/listing/${listingSlug || listingId}`);
  };

  // Handle boost click
  const handleBoost = async () => {
    setBoosting(true);
    try {
      // Use the 24h package as default
      const response = await axios.post(
        `${API}/payments/boost/create`,
        null,
        { params: { listing_id: listingId, package_id: '24h' } }
      );
      
      if (response.data.success) {
        if (response.data.payment_url) {
          window.location.href = response.data.payment_url;
        } else {
          toast.success(tb('boost_requested'));
        }
      } else {
        const message = response.data.status === 'not_configured' 
          ? tb('payment_coming_soon')
          : tb('boost_not_available');
        toast.info(message);
        setBoosting(false);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || tb('boost_error');
      toast.error(errorMessage);
      setBoosting(false);
    }
  };

  // Handle close / later
  const handleLater = () => {
    onClose();
    navigate(`/listing/${listingSlug || listingId}`);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[150] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={handleLater}
      />

      {/* Confetti Animation */}
      {showConfetti && (
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {[...Array(50)].map((_, i) => (
            <div
              key={i}
              className="absolute animate-confetti"
              style={{
                left: `${Math.random() * 100}%`,
                top: '-10px',
                animationDelay: `${Math.random() * 2}s`,
                animationDuration: `${2 + Math.random() * 2}s`
              }}
            >
              <div
                className="w-3 h-3 rotate-45"
                style={{
                  backgroundColor: ['#FFD700', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD', '#98D8C8'][Math.floor(Math.random() * 8)]
                }}
              />
            </div>
          ))}
        </div>
      )}

      {/* Modal Content */}
      <div className="relative bg-background border border-border rounded-2xl w-full max-w-md overflow-hidden shadow-2xl animate-in zoom-in-95 duration-300">
        {/* Close button */}
        <button
          onClick={handleLater}
          className="absolute top-4 right-4 p-2 hover:bg-secondary rounded-full transition-colors z-10"
        >
          <X className="w-5 h-5 text-muted-foreground" />
        </button>

        {/* Success Header */}
        <div className="pt-8 pb-6 px-6 text-center bg-gradient-to-b from-green-500/10 to-transparent">
          <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-green-500/20 flex items-center justify-center">
            <CheckCircle className="w-12 h-12 text-green-500" />
          </div>
          <h2 className="text-2xl font-bold text-foreground mb-2">
            Lekker bezig! 🌟
          </h2>
          <p className="text-muted-foreground">
            Je advertentie staat live. Deel hem nu met je vrienden en familie om sneller een koper te vinden.
          </p>
        </div>

        {/* Share Section */}
        <div className="px-6 pb-6">
          <p className="text-sm font-medium text-foreground mb-4 text-center">
            Deel je advertentie en verkoop sneller!
          </p>

          {/* Native Share Button (Mobile) */}
          {canNativeShare && (
            <button
              onClick={handleNativeShare}
              className="w-full mb-4 py-3 px-4 bg-primary text-primary-foreground rounded-xl font-medium flex items-center justify-center gap-2 hover:opacity-90 transition-opacity"
            >
              <Share2 className="w-5 h-5" />
              Delen via...
            </button>
          )}

          {/* Share Buttons Grid */}
          <div className={`grid ${canNativeShare ? 'grid-cols-5' : 'grid-cols-4'} gap-3 mb-4`}>
            {SHARE_BUTTONS.map((button) => {
              const IconComponent = button.icon;
              return (
                <button
                  key={button.id}
                  onClick={() => handleShare(button)}
                  className={`${button.color} text-white p-3 rounded-xl flex items-center justify-center transition-all hover:scale-105 active:scale-95`}
                  title={button.name}
                >
                  {typeof IconComponent === 'function' ? <IconComponent /> : <IconComponent className="w-5 h-5" />}
                </button>
              );
            })}

            {/* Copy Link Button */}
            <button
              onClick={handleCopyLink}
              className={`${copied ? 'bg-green-500' : 'bg-gray-500 hover:bg-gray-600'} text-white p-3 rounded-xl flex items-center justify-center transition-all hover:scale-105 active:scale-95`}
              title="Kopieer link"
            >
              {copied ? (
                <CheckCircle className="w-5 h-5" />
              ) : (
                <Copy className="w-5 h-5" />
              )}
            </button>
          </div>

          {/* View Listing CTA */}
          <button
            onClick={handleViewListing}
            className="w-full py-3 px-4 bg-secondary hover:bg-secondary/80 text-foreground rounded-xl font-medium flex items-center justify-center gap-2 transition-colors"
          >
            <ExternalLink className="w-5 h-5" />
            {tb('view_my_listing')}
          </button>

          {/* Boost Section - Coming Soon */}
          <div className="mt-4 pt-4 border-t border-border">
            <ComingSoonOverlay 
              title={tb('coming_soon')} 
              subtitle={tb('boost_visibility')}
              icon={Zap}
              useBoostEnabled={true}
            >
              <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/30 rounded-xl">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white">
                  <Zap className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground">{tb('boost_your_listing')}</p>
                  <p className="text-xs text-muted-foreground">{tb('get_more_visitors')}</p>
                </div>
                <button 
                  onClick={handleBoost}
                  disabled={boosting}
                  className="px-4 py-2 text-sm font-medium bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
                >
                  {boosting ? '...' : tb('boost')}
                </button>
              </div>
            </ComingSoonOverlay>
          </div>

          {/* Later Option */}
          <button
            onClick={handleLater}
            className="w-full mt-3 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            {tb('share_later')}
          </button>
        </div>
      </div>

      {/* Confetti CSS */}
      <style>{`
        @keyframes confetti {
          0% {
            transform: translateY(0) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(100vh) rotate(720deg);
            opacity: 0;
          }
        }
        .animate-confetti {
          animation: confetti linear forwards;
        }
      `}</style>
    </div>
  );
};

export default ShareSuccessModal;
